(() => {
  const MAX_METER_COUNT = 8;
  const INITIAL_CATALOG_BATCH_SIZE = 48;
  const CATALOG_BATCH_SIZE = 48;
  const CORAL_CATEGORIES = [
    {
      key: 'sps',
      label: 'SPS Corals',
      tags: ['SPS', 'Acropora', 'Montipora', 'Birdsnest', 'Stylophora', 'Pocillopora'],
    },
    {
      key: 'soft',
      label: 'Soft Corals',
      tags: ['Soft Coral', 'Zoanthids', 'Green Star Polyps', 'Leather', 'Xenia'],
    },
    {
      key: 'lps',
      label: 'LPS Corals',
      tags: [
        'LPS',
        'Cyphastrea',
        'Favia',
        'Chalice',
        'Leptoseris',
        'Leptastrea',
        'Galaxea',
        'Alveopora',
        'Blastomussa',
        'Goniopora',
        'Acans',
        'Plate Coral',
        'Lithophyllon',
      ],
    },
  ];
  const OTHER_CORAL_CATEGORY = { key: 'other', label: 'Other Corals', tags: [] };
  const ACTIVE_CATEGORY_LINE_OFFSET = 24;
  const REVEAL_STAGGER_MS = 55;
  const REVEAL_STAGGER_LIMIT = 12;
  const REVEAL_TARGET_SELECTOR =
    '.topshelf-coral-pack__card:not(.is-revealed), .topshelf-coral-pack__category-heading:not(.is-revealed)';

  class TopShelfCoralPackBuilder extends HTMLElement {
    constructor() {
      super();
      this.selected = new Map();
      this.busy = false;
      this.lastRenderedCount = null;
      this.catalogProducts = [];
      this.catalogRenderIndex = 0;
      this.renderedCatalogCount = 0;
      this.pendingCatalogRender = null;
      this.categoryNavLinks = new Map();
      this.activeCategoryKey = null;
      this.categoryNavLockKey = null;
      this.activeCategoryFrame = null;
      this.revealObserver = null;
      this.categorySections = new Map();
      this.renderedSelectionIds = null;
      this.abortController = new AbortController();
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.config = this.readConfig();
      this.storageKey = this.dataset.storageKey || 'topshelf-coral-pack';
      this.currencyFormatter = new Intl.NumberFormat(this.config.locale || undefined, {
        style: 'currency',
        currency: this.config.currency || 'USD',
      });

      this.cacheElements();
      this.setupReveal();
      this.restore();
      const { signal } = this.abortController;
      this.addEventListener('click', this.onClick, { signal });
      // Desktop scrolls `.page-wrapper` instead of the window; capture catches both.
      document.addEventListener('scroll', this.scheduleActiveCategoryUpdate, {
        capture: true,
        passive: true,
        signal,
      });
      ['wheel', 'touchstart', 'keydown'].forEach((type) => {
        window.addEventListener(type, this.releaseCategoryNavLock, { passive: true, signal });
      });
      this.render();
      this.loadAndGroupAllCorals();
    }

    disconnectedCallback() {
      this.cancelCatalogRender();
      if (this.activeCategoryFrame) window.cancelAnimationFrame(this.activeCategoryFrame);
      this.revealObserver?.disconnect();
      this.abortController.abort();
    }

    setupReveal() {
      if (!('IntersectionObserver' in window)) return;
      this.revealObserver = new IntersectionObserver(this.onReveal, { rootMargin: '0px 0px -6% 0px' });
      this.dataset.packMotion = 'ready';
    }

    onReveal = (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .sort(
          (left, right) =>
            Math.round(left.boundingClientRect.top) - Math.round(right.boundingClientRect.top) ||
            left.boundingClientRect.left - right.boundingClientRect.left
        )
        .forEach((entry, index) => {
          const target = entry.target;
          this.revealObserver.unobserve(target);
          target.style.setProperty(
            '--pack-reveal-delay',
            `${Math.min(index, REVEAL_STAGGER_LIMIT) * REVEAL_STAGGER_MS}ms`
          );
          target.classList.add('is-revealed');
        });
    };

    observeReveal() {
      if (!this.revealObserver || !this.catalogElement) return;
      this.catalogElement
        .querySelectorAll(REVEAL_TARGET_SELECTOR)
        .forEach((node) => this.revealObserver.observe(node));
    }

    readConfig() {
      const element = this.querySelector('[data-pack-config]');
      if (!element) return {};

      try {
        return JSON.parse(element.textContent || '{}');
      } catch (error) {
        console.error('Unable to read Coral Pack Builder configuration.', error);
        return {};
      }
    }

    cacheElements() {
      this.countElement = this.querySelector('[data-pack-count]');
      this.pageCountElement = this.querySelector('[data-page-selection-count]');
      this.meterElement = this.querySelector('[data-pack-meter]');
      this.meterFillElement = this.querySelector('[data-pack-meter-fill]');
      this.milestoneElements = this.querySelectorAll('[data-pack-milestone]');
      this.tierStatusElement = this.querySelector('[data-tier-status]');
      this.selectedListElement = this.querySelector('[data-selected-list]');
      this.subtotalElement = this.querySelector('[data-pack-subtotal]');
      this.discountElement = this.querySelector('[data-pack-discount]');
      this.totalElement = this.querySelector('[data-pack-total]');
      this.addButton = this.querySelector('[data-add-pack]');
      this.addButtonLabel = this.querySelector('[data-add-pack-label]');
      this.statusElement = this.querySelector('[data-pack-status]');
      this.catalogStatusElement = this.querySelector('[data-catalog-status]');
      this.catalogElement = this.querySelector('[data-coral-catalog]');
      this.gridElement = this.querySelector('[data-coral-grid]');
      this.summaryElement = this.querySelector('.topshelf-coral-pack__summary');
      this.summaryHeadingElement = this.querySelector('[id^="CoralPackSummary-"]');
      this.mobileDockElement = this.querySelector('[data-mobile-pack-dock]');
      this.mobileCountElement = this.querySelector('[data-mobile-pack-count]');
      this.mobileTierStatusElement = this.querySelector('[data-mobile-tier-status]');
      this.mobileMeterElement = this.querySelector('[data-mobile-pack-meter]');
      this.mobileMeterFillElement = this.querySelector('[data-mobile-pack-meter-fill]');
      this.categoryNavElement = this.querySelector('[data-category-nav]');
      this.categoryNavListElement = this.querySelector('[data-category-nav-list]');
    }

    onClick = (event) => {
      const categoryLink = event.target.closest('[data-category-nav-link]');
      if (categoryLink && this.contains(categoryLink)) {
        event.preventDefault();
        this.scrollToCategory(categoryLink.dataset.categoryNavLink);
        return;
      }

      const selectButton = event.target.closest('[data-select-coral]');
      if (selectButton && this.contains(selectButton)) {
        this.toggleCard(selectButton.closest('[data-coral-variant-id]'));
        return;
      }

      const removeButton = event.target.closest('[data-remove-variant]');
      if (removeButton && this.contains(removeButton)) {
        this.removeSelection(removeButton.dataset.removeVariant);
        return;
      }

      const viewPackButton = event.target.closest('[data-view-pack]');
      if (viewPackButton && this.contains(viewPackButton)) {
        this.scrollToSummary();
        return;
      }

      const addButton = event.target.closest('[data-add-pack]');
      if (addButton && this.contains(addButton)) {
        this.addPack();
      }
    };

    toggleCard(card) {
      if (!card) return;

      const variantId = card.dataset.coralVariantId;
      if (!variantId) return;

      if (this.selected.has(variantId)) {
        this.selected.delete(variantId);
      } else {
        this.selected.set(variantId, this.selectionFromCard(card));
      }

      this.clearStatus();
      this.persist();
      this.render();
    }

    removeSelection(variantId) {
      if (!variantId) return;
      this.selected.delete(variantId);
      this.clearStatus();
      this.persist();
      this.render();
    }

    selectionFromCard(card) {
      const priceCents = Number.parseInt(card.dataset.priceCents || '0', 10) || 0;
      return {
        id: String(card.dataset.coralVariantId || ''),
        title: card.dataset.variantTitle || 'Coral',
        priceCents,
        currentPriceCents: Number.parseInt(card.dataset.currentPriceCents || '', 10) || priceCents,
        sourceUrl: card.dataset.sourceUrl || `${window.location.pathname}${window.location.search}`,
      };
    }

    restore() {
      let stored;

      try {
        stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || '[]');
      } catch (error) {
        stored = [];
      }

      if (!Array.isArray(stored)) return;

      stored.forEach((item) => {
        if (!item || !item.id || this.selected.has(String(item.id))) return;
        const priceCents = Number.parseInt(item.priceCents, 10) || 0;
        this.selected.set(String(item.id), {
          id: String(item.id),
          title: String(item.title || 'Coral'),
          priceCents,
          currentPriceCents: Number.parseInt(item.currentPriceCents, 10) || priceCents,
          sourceUrl: String(item.sourceUrl || `${window.location.pathname}${window.location.search}`),
        });
      });
    }

    persist() {
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(Array.from(this.selected.values())));
      } catch (error) {
        // Selection still works for this page when storage is unavailable.
      }
    }

    clearPersistedSelection() {
      this.selected.clear();
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        // Nothing else is required when storage is unavailable.
      }
    }

    render() {
      const count = this.selected.size;
      const discountPercent = this.discountPercentForCount(count);
      const selectedItems = Array.from(this.selected.values());
      const subtotalCents = selectedItems.reduce((total, item) => total + item.priceCents, 0);
      // Keep in sync with the discount Function: pack pricing starts from the regular price,
      // and a coral already on a deeper sale keeps its sale price instead of stacking.
      const totalCents = selectedItems.reduce(
        (total, item) =>
          total +
          Math.min(item.currentPriceCents, Math.round((item.priceCents * (100 - discountPercent)) / 100)),
        0
      );
      const discountCents = subtotalCents - totalCents;
      const progress = Math.min(count / MAX_METER_COUNT, 1) * 100;

      if (this.countElement) this.countElement.textContent = String(count);
      if (this.meterFillElement) this.meterFillElement.style.width = `${progress}%`;
      if (this.meterElement) {
        this.meterElement.setAttribute('aria-valuenow', String(Math.min(count, MAX_METER_COUNT)));
        this.meterElement.setAttribute(
          'aria-valuetext',
          `${count} corals selected. ${discountPercent}% pack discount.`
        );
      }
      this.renderMilestones(count);
      if (this.tierStatusElement) this.tierStatusElement.textContent = this.tierMessage(count);
      if (this.subtotalElement) this.subtotalElement.textContent = this.money(subtotalCents);
      if (this.discountElement) this.discountElement.textContent = `−${this.money(discountCents)}`;
      if (this.totalElement) this.totalElement.textContent = this.money(totalCents);
      if (this.mobileDockElement) this.mobileDockElement.hidden = count === 0;
      if (this.mobileCountElement) {
        this.mobileCountElement.textContent = `${count} ${count === 1 ? 'coral' : 'corals'} selected`;
      }
      if (this.mobileTierStatusElement) {
        this.mobileTierStatusElement.textContent = this.mobileTierMessage(count);
      }
      if (this.mobileMeterFillElement) this.mobileMeterFillElement.style.width = `${progress}%`;
      if (this.mobileMeterElement) {
        this.mobileMeterElement.setAttribute('aria-valuenow', String(Math.min(count, MAX_METER_COUNT)));
        this.mobileMeterElement.setAttribute(
          'aria-valuetext',
          `${count} corals selected. ${discountPercent}% pack discount.`
        );
      }

      this.renderCards();
      this.renderSelectedList();
      this.renderPageCount();

      if (this.addButton) this.addButton.disabled = this.busy || count === 0;
      if (this.addButtonLabel && !this.busy) {
        this.addButtonLabel.textContent = count
          ? `${this.config.addLabel || 'Add pack to cart'} (${count})`
          : this.config.addLabel || 'Add pack to cart';
      }

      this.lastRenderedCount = count;
    }

    renderMilestones(count) {
      this.milestoneElements.forEach((element) => {
        const milestone = Number.parseInt(element.dataset.packMilestone || '0', 10);
        const isUnlocked = count >= milestone;
        const wasUnlocked = this.lastRenderedCount !== null && this.lastRenderedCount >= milestone;

        element.classList.toggle('is-unlocked', isUnlocked);

        if (isUnlocked && !wasUnlocked && this.lastRenderedCount !== null) {
          element.classList.remove('is-celebrating');
          void element.offsetWidth;
          element.classList.add('is-celebrating');
        }
      });
    }

    renderCards() {
      this.querySelectorAll('[data-coral-variant-id]').forEach((card) => {
        const isSelected = this.selected.has(String(card.dataset.coralVariantId || ''));
        const button = card.querySelector('[data-select-coral]');
        if (!button) return;

        button.setAttribute('aria-pressed', String(isSelected));
        const selectLabel = button.querySelector('[data-select-label]');
        const selectedLabel = button.querySelector('[data-selected-label]');
        if (selectLabel) selectLabel.hidden = isSelected;
        if (selectedLabel) selectedLabel.hidden = !isSelected;
      });
    }

    renderSelectedList() {
      if (!this.selectedListElement) return;

      const previousIds = this.renderedSelectionIds;
      this.renderedSelectionIds = new Set(this.selected.keys());

      const fragment = document.createDocumentFragment();
      this.selected.forEach((item) => {
        const listItem = document.createElement('li');
        listItem.className = 'topshelf-coral-pack__selected-item';
        if (previousIds && !previousIds.has(item.id)) listItem.classList.add('is-entering');

        const title = document.createElement('span');
        title.textContent = item.title;
        title.title = item.title;

        const removeButton = document.createElement('button');
        removeButton.className = 'topshelf-coral-pack__selected-remove';
        removeButton.type = 'button';
        removeButton.dataset.removeVariant = item.id;
        removeButton.setAttribute('aria-label', `Remove ${item.title} from pack`);
        removeButton.textContent = '×';

        listItem.append(title, removeButton);
        fragment.append(listItem);
      });

      this.selectedListElement.replaceChildren(fragment);
    }

    renderPageCount() {
      if (!this.pageCountElement) return;

      const visibleSelected = Array.from(this.querySelectorAll('[data-coral-variant-id]')).filter((card) =>
        this.selected.has(String(card.dataset.coralVariantId || ''))
      ).length;

      this.pageCountElement.hidden = visibleSelected === 0;
      this.pageCountElement.textContent = `${visibleSelected} selected`;
    }

    discountPercentForCount(count) {
      if (count >= 8) return 20;
      if (count >= 6) return 15;
      if (count >= 4) return 10;
      return 0;
    }

    tierMessage(count) {
      if (count >= 8) return this.config.tierLabels?.twenty || '20% unlocked — top tier reached.';
      if (count >= 6) {
        return `${this.config.tierLabels?.fifteen || '15% unlocked'} · ${8 - count} more for 20%.`;
      }
      if (count >= 4) {
        return `${this.config.tierLabels?.ten || '10% unlocked'} · ${6 - count} more for 15%.`;
      }
      const remaining = 4 - count;
      return `${remaining} more ${remaining === 1 ? 'coral' : 'corals'} to unlock 10% off.`;
    }

    mobileTierMessage(count) {
      if (count >= 8) return '20% discount unlocked';
      if (count >= 6) return `15% unlocked · ${8 - count} more for 20%`;
      if (count >= 4) return `10% unlocked · ${6 - count} more for 15%`;
      return `${4 - count} more for 10% off`;
    }

    scrollToSummary() {
      if (!this.summaryElement) return;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.summaryHeadingElement?.focus({ preventScroll: true });
      this.summaryElement.scrollIntoView({ behavior, block: 'start' });
    }

    categorySectionId(key) {
      return `${this.id}-${key}`;
    }

    scrollToCategory(key) {
      const section = this.catalogElement?.querySelector(`[data-coral-category="${key}"]`);
      if (!section) return;

      const nav = this.categoryNavElement && !this.categoryNavElement.hidden ? this.categoryNavElement : null;
      const navRect = nav ? nav.getBoundingClientRect() : null;
      // Scrolling up slides the header back in above the sticky nav, so leave room for it.
      const isScrollingUp = section.getBoundingClientRect().top < (navRect ? navRect.bottom : 0);
      const headerOffset = isScrollingUp
        ? Number.parseFloat(
            getComputedStyle(document.body).getPropertyValue('--kith-header-bottom') ||
              getComputedStyle(document.body).getPropertyValue('--kith-header-offset')
          ) || 0
        : 0;
      const navHeight = navRect ? navRect.height : 0;
      this.style.setProperty('--pack-category-scroll-offset', `${Math.round(headerOffset + navHeight)}px`);

      this.categoryNavLockKey = key;
      this.setActiveCategory(key);

      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      section.querySelector('.topshelf-coral-pack__category-heading')?.focus({ preventScroll: true });
      section.scrollIntoView({ behavior, block: 'start' });
    }

    renderCategoryNav(groupCounts) {
      if (!this.categoryNavElement || !this.categoryNavListElement) return;

      if (!this.categoryNavLinks.size) {
        [...CORAL_CATEGORIES, OTHER_CORAL_CATEGORY].forEach((category) => {
          const link = document.createElement('a');
          link.className = 'topshelf-coral-pack__quick-nav-link';
          link.href = `#${this.categorySectionId(category.key)}`;
          link.dataset.categoryNavLink = category.key;
          link.hidden = true;
          const label = document.createElement('span');
          label.textContent = category.label;
          const count = document.createElement('small');
          link.append(label, count);
          this.categoryNavLinks.set(category.key, link);
          this.categoryNavListElement.append(link);
        });
      }

      let visibleCount = 0;
      this.categoryNavLinks.forEach((link, key) => {
        const count = groupCounts.get(key) || 0;
        link.hidden = count === 0;
        if (count) visibleCount += 1;
        const countElement = link.querySelector('small');
        countElement.textContent = String(count);
        countElement.setAttribute('aria-label', `${count} corals`);
      });

      this.categoryNavElement.hidden = visibleCount < 2;
    }

    scheduleActiveCategoryUpdate = () => {
      if (this.activeCategoryFrame) return;
      this.activeCategoryFrame = window.requestAnimationFrame(() => {
        this.activeCategoryFrame = null;
        this.updateActiveCategory();
      });
    };

    releaseCategoryNavLock = () => {
      if (!this.categoryNavLockKey) return;
      this.categoryNavLockKey = null;
      this.scheduleActiveCategoryUpdate();
    };

    updateActiveCategory() {
      if (!this.catalogElement || !this.categoryNavElement || this.categoryNavElement.hidden) return;
      if (this.categoryNavLockKey) return;

      const line = this.categoryNavElement.getBoundingClientRect().bottom + ACTIVE_CATEGORY_LINE_OFFSET;
      let activeKey = null;
      this.catalogElement.querySelectorAll('[data-coral-category]').forEach((section) => {
        if (!activeKey || section.getBoundingClientRect().top <= line) {
          activeKey = section.dataset.coralCategory;
        }
      });
      this.setActiveCategory(activeKey);
    }

    setActiveCategory(key) {
      if (!key || key === this.activeCategoryKey) return;
      this.activeCategoryKey = key;

      this.categoryNavLinks.forEach((link, linkKey) => {
        if (linkKey === key) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      const list = this.categoryNavListElement;
      const activeLink = this.categoryNavLinks.get(key);
      if (list && activeLink && list.scrollWidth > list.clientWidth) {
        list.scrollTo({
          left: activeLink.offsetLeft - (list.clientWidth - activeLink.offsetWidth) / 2,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }

    money(cents) {
      return this.currencyFormatter.format((Number(cents) || 0) / 100);
    }

    setStatus(message, isError = false) {
      if (!this.statusElement) return;
      this.statusElement.textContent = message;
      this.statusElement.dataset.error = String(isError);
    }

    clearStatus() {
      if (!this.statusElement) return;
      this.statusElement.textContent = '';
      this.statusElement.removeAttribute('data-error');
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      this.toggleAttribute('aria-busy', isBusy);
      if (this.addButtonLabel && isBusy) {
        this.addButtonLabel.textContent = this.config.addingLabel || 'Adding pack…';
      }
      this.render();
    }

    async addPack() {
      if (this.busy || this.selected.size === 0) return;

      this.clearStatus();
      this.setBusy(true);

      try {
        const packId = this.createPackId();
        const items = Array.from(this.selected.values()).map((item) => ({
          id: Number(item.id),
          quantity: 1,
          properties: {
            _topshelf_pack_id: packId,
            _topshelf_pack_source: this.config.source || 'coral-pack-builder-v1',
          },
        }));
        const sectionIds = this.cartSectionIds();
        const body = { items };

        if (sectionIds.length) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }

        const addResponse = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const addResult = await this.parseJsonResponse(addResponse);

        if (!addResponse.ok || addResult.status) {
          throw new Error(addResult.description || addResult.message || 'The coral pack could not be added.');
        }

        const cartResponse = await fetch(this.dataset.cartUrl || '/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const cart = await this.parseJsonResponse(cartResponse);
        if (!cartResponse.ok) throw new Error('The cart could not be refreshed.');

        const addedCount = this.selected.size;
        this.clearPersistedSelection();
        this.render();
        await this.dispatchCartUpdate(cart, addResult.sections || {}, items, addedCount);
        this.setStatus(this.config.successMessage || 'Your coral pack was added to the cart.');
      } catch (error) {
        this.setStatus(error instanceof Error ? error.message : 'The coral pack could not be added.', true);
      } finally {
        this.setBusy(false);
      }
    }

    async parseJsonResponse(response) {
      const text = await response.text();
      try {
        return JSON.parse(text || '{}');
      } catch (error) {
        return {
          status: response.status,
          message: response.ok ? 'Shopify returned an unexpected response.' : response.statusText,
        };
      }
    }

    async loadAndGroupAllCorals() {
      if (!this.catalogElement || !this.gridElement) {
        if (this.catalogStatusElement) this.catalogStatusElement.textContent = '';
        return;
      }

      try {
        const catalogUrl = this.config.catalogUrl;
        if (!catalogUrl) throw new Error('The coral catalog is not configured.');
        const response = await fetch(catalogUrl, {
          mode: 'cors',
          cache: 'no-store',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        const payload = await this.parseJsonResponse(response);
        if (!response.ok || !Array.isArray(payload.products)) {
          throw new Error(payload.message || 'The eligible coral catalog could not be loaded.');
        }

        this.cancelCatalogRender();
        this.catalogProducts = payload.products;
        this.catalogRenderIndex = 0;
        this.renderedCatalogCount = 0;
        this.categorySections.clear();
        this.catalogElement.replaceChildren();
        this.renderCatalogBatch(INITIAL_CATALOG_BATCH_SIZE);
      } catch (error) {
        this.cancelCatalogRender();
        if (this.catalogStatusElement) {
          this.catalogStatusElement.textContent =
            'Eligible corals could not be loaded. Refresh the page to try again.';
          this.catalogStatusElement.dataset.error = 'true';
        }
      }
    }

    renderCatalogBatch(batchSize = CATALOG_BATCH_SIZE) {
      if (!this.catalogElement) return;

      const products = this.catalogProducts.slice(
        this.catalogRenderIndex,
        this.catalogRenderIndex + batchSize
      );
      this.catalogRenderIndex += products.length;

      const cards = products.map((product) => this.cardForProduct(product)).filter(Boolean);
      this.renderedCatalogCount += cards.length;
      this.groupAndSortCorals(cards);
      this.render();

      const hasMore = this.catalogRenderIndex < this.catalogProducts.length;
      if (this.catalogStatusElement) {
        if (hasMore && this.catalogRenderIndex === products.length) {
          this.catalogStatusElement.textContent =
            `${this.renderedCatalogCount} corals ready. Loading the remaining corals…`;
        } else if (!hasMore) {
          this.catalogStatusElement.textContent = this.renderedCatalogCount
            ? `${this.renderedCatalogCount} eligible corals, grouped by type.`
            : this.config.emptyMessage || 'No eligible corals are currently available.';
        }
      }

      if (hasMore) this.scheduleCatalogRender();
    }

    scheduleCatalogRender() {
      if (this.pendingCatalogRender) return;

      const renderNextBatch = () => {
        this.pendingCatalogRender = null;
        this.renderCatalogBatch();
      };

      if ('requestIdleCallback' in window) {
        const id = window.requestIdleCallback(renderNextBatch, { timeout: 500 });
        this.pendingCatalogRender = { type: 'idle', id };
      } else {
        const id = window.setTimeout(renderNextBatch, 16);
        this.pendingCatalogRender = { type: 'timeout', id };
      }
    }

    cancelCatalogRender() {
      if (!this.pendingCatalogRender) return;

      if (
        this.pendingCatalogRender.type === 'idle' &&
        'cancelIdleCallback' in window
      ) {
        window.cancelIdleCallback(this.pendingCatalogRender.id);
      } else {
        window.clearTimeout(this.pendingCatalogRender.id);
      }
      this.pendingCatalogRender = null;
    }

    cardForProduct(product) {
      const variantId = String(product?.variantId || '');
      const title = String(product?.title || '').trim();
      const priceCents = Number.parseInt(product?.priceCents, 10);
      if (!/^\d+$/.test(variantId) || !title || !Number.isFinite(priceCents)) return null;

      const variantTitle = String(product?.variantTitle || '').trim();
      const variantLabel =
        variantTitle && !['Regular', 'Default Title'].includes(variantTitle)
          ? `${title} — ${variantTitle}`
          : title;
      const productUrl =
        typeof product.productUrl === 'string' && product.productUrl.startsWith('/products/')
          ? product.productUrl
          : '#';

      const card = document.createElement('article');
      card.className = 'topshelf-coral-pack__card';
      card.dataset.coralVariantId = variantId;
      card.dataset.variantTitle = variantLabel;
      card.dataset.priceCents = String(priceCents);
      const currentPriceCents = Number.parseInt(product?.currentPriceCents, 10);
      card.dataset.currentPriceCents = String(Number.isFinite(currentPriceCents) ? currentPriceCents : priceCents);
      card.dataset.coralTags = JSON.stringify(Array.isArray(product.tags) ? product.tags : []);
      card.dataset.sourceUrl = productUrl;

      const media = document.createElement('a');
      media.className = 'topshelf-coral-pack__media';
      media.href = productUrl;
      media.setAttribute('aria-label', variantLabel);
      if (typeof product.imageUrl === 'string' && product.imageUrl) {
        const image = document.createElement('img');
        image.className = 'topshelf-coral-pack__image';
        image.src = product.imageUrl;
        image.alt = title;
        image.loading = 'lazy';
        image.decoding = 'async';
        media.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'topshelf-coral-pack__image topshelf-coral-pack__placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        media.append(placeholder);
      }

      const body = document.createElement('div');
      body.className = 'topshelf-coral-pack__card-body';
      const headingWrap = document.createElement('div');
      const heading = document.createElement('h3');
      heading.className = 'topshelf-coral-pack__product-title';
      const productLink = document.createElement('a');
      productLink.href = productUrl;
      productLink.textContent = title;
      heading.append(productLink);
      headingWrap.append(heading);
      if (variantTitle && !['Regular', 'Default Title'].includes(variantTitle)) {
        const subtitle = document.createElement('p');
        subtitle.className = 'topshelf-coral-pack__variant-title';
        subtitle.textContent = variantTitle;
        headingWrap.append(subtitle);
      }

      const meta = document.createElement('div');
      meta.className = 'topshelf-coral-pack__card-meta';
      const price = document.createElement('span');
      price.className = 'topshelf-coral-pack__price';
      price.textContent = this.money(priceCents);
      meta.append(price);

      const button = document.createElement('button');
      button.className = 'topshelf-coral-pack__select';
      button.type = 'button';
      button.setAttribute('aria-pressed', 'false');
      button.dataset.selectCoral = '';
      const selectLabel = document.createElement('span');
      selectLabel.dataset.selectLabel = '';
      selectLabel.textContent = this.config.selectLabel || 'Add to pack';
      const selectedLabel = document.createElement('span');
      selectedLabel.dataset.selectedLabel = '';
      selectedLabel.textContent = this.config.selectedLabel || 'Selected';
      selectedLabel.hidden = true;
      button.append(selectLabel, selectedLabel);

      body.append(headingWrap, meta, button);
      card.append(media, body);
      return card;
    }

    groupAndSortCorals(newCards = []) {
      if (!this.catalogElement) return;

      const cards = [
        ...Array.from(this.catalogElement.querySelectorAll('[data-coral-variant-id]')),
        ...newCards,
      ];
      const groups = new Map();
      const groupCounts = new Map();

      cards.forEach((card) => {
        const category = this.categoryForCard(card) || OTHER_CORAL_CATEGORY;
        if (!groups.has(category.label)) groups.set(category.label, { category, cards: [] });
        groups.get(category.label).cards.push(card);
      });

      // Placed cards and sections are never detached: moving a node restarts its reveal
      // transition, so only new cards are inserted into their sorted position.
      let previousSection = null;
      [...CORAL_CATEGORIES, OTHER_CORAL_CATEGORY].forEach((category, categoryIndex) => {
        const group = groups.get(category.label);
        if (!group) return;
        groupCounts.set(category.key, group.cards.length);

        group.cards.sort((left, right) =>
          (left.dataset.variantTitle || '').localeCompare(
            right.dataset.variantTitle || '',
            this.config.locale || undefined,
            { sensitivity: 'base' }
          )
        );

        const categorySection = this.categorySectionFor(category, categoryIndex);
        const count = categorySection.querySelector('.topshelf-coral-pack__category-heading small');
        count.textContent = String(group.cards.length);
        count.setAttribute('aria-label', `${group.cards.length} corals`);

        const grid = categorySection.querySelector('.topshelf-coral-pack__grid');
        let cursor = grid.firstElementChild;
        group.cards.forEach((card) => {
          if (card === cursor) {
            cursor = cursor.nextElementSibling;
            return;
          }
          grid.insertBefore(card, cursor);
        });

        const expectedPosition = previousSection
          ? previousSection.nextElementSibling
          : this.catalogElement.firstElementChild;
        if (categorySection !== expectedPosition) {
          this.catalogElement.insertBefore(categorySection, expectedPosition);
        }
        previousSection = categorySection;
      });

      this.observeReveal();
      this.renderCategoryNav(groupCounts);
      this.updateActiveCategory();
    }

    categorySectionFor(category, categoryIndex) {
      const existing = this.categorySections.get(category.key);
      if (existing) return existing;

      const categorySection = document.createElement('section');
      categorySection.className = 'topshelf-coral-pack__category';
      categorySection.dataset.coralCategory = category.key;
      categorySection.id = this.categorySectionId(category.key);

      const headingId = `${this.id}-category-${categoryIndex}`;
      const heading = document.createElement('h3');
      heading.className = 'topshelf-coral-pack__category-heading';
      heading.id = headingId;
      heading.tabIndex = -1;

      const label = document.createElement('span');
      label.textContent = category.label;
      const count = document.createElement('small');
      heading.append(label, count);

      const grid = document.createElement('div');
      grid.className = 'topshelf-coral-pack__grid';
      grid.setAttribute('aria-labelledby', headingId);

      categorySection.append(heading, grid);
      this.categorySections.set(category.key, categorySection);
      return categorySection;
    }

    categoryForCard(card) {
      let tags = [];
      try {
        tags = JSON.parse(card.dataset.coralTags || '[]');
      } catch (error) {
        tags = [];
      }
      return CORAL_CATEGORIES.find((category) =>
        category.tags.some((categoryTag) => tags.includes(categoryTag))
      );
    }

    cartSectionIds() {
      const ids = new Set();
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
        if (component.dataset.sectionId) ids.add(component.dataset.sectionId);
      });
      return Array.from(ids);
    }

    createPackId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      const random = window.crypto?.getRandomValues
        ? window.crypto.getRandomValues(new Uint32Array(2)).join('')
        : Math.random().toString(36).slice(2);
      return `pack-${Date.now().toString(36)}-${random}`;
    }

    async dispatchCartUpdate(cart, sections, items, addedCount) {
      // This file is a classic script, so the module loads through the theme import map.
      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
            promise: deferred.promise,
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'topshelf-coral-pack-builder',
            sourceId: this.id,
            itemCount: addedCount,
            sections,
            didError: false,
          },
        });
      } catch (error) {
        // The pack is already in the cart; only the live cart UI refresh failed.
        console.error('Unable to refresh the cart UI after adding the coral pack.', error);
      }
    }
  }

  if (!customElements.get('topshelf-coral-pack-builder')) {
    customElements.define('topshelf-coral-pack-builder', TopShelfCoralPackBuilder);
  }
})();
